import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import {
    CreateServiceAccountTokenRequest,
    DeleteServiceAccountTokenRequest,
    ListServiceAccountTokensRequest,
} from "gitpod-next-api/gitpod/v1/service_account_pb";
import type { ServiceAccountToken } from "gitpod-next-api/gitpod/v1/service_account_pb";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { ResourceOperation, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import type { ServiceAccountHeaders } from "@/api";

export type PlainServiceAccountToken = PlainMessage<ServiceAccountToken>;

const toPlainServiceAccountToken = (token: ServiceAccountToken): PlainServiceAccountToken => {
    return toPlainMessage(token);
};

export const serviceAccountTokensQueryKey = {
    list: (serviceAccountId: string) => keyWithPrincipal(["service-account-tokens", "list", serviceAccountId]),
};

export const handleServiceAccountTokenEvent = async (
    client: QueryClient,
    evt: WatchEventsResponse,
    serviceAccountId: string,
) => {
    switch (evt.operation) {
        case ResourceOperation.CREATE:
        case ResourceOperation.DELETE: {
            await client.invalidateQueries({ queryKey: serviceAccountTokensQueryKey.list(serviceAccountId) });
            break;
        }
    }
};

export type UseListServiceAccountTokensParams = {
    serviceAccountId: string;
    headers?: ServiceAccountHeaders;
    enabled?: boolean;
};

export const useListServiceAccountTokens = ({
    serviceAccountId,
    headers,
    enabled = true,
}: UseListServiceAccountTokensParams) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: serviceAccountTokensQueryKey.list(serviceAccountId),
        queryFn: async () => {
            const request = new ListServiceAccountTokensRequest({});
            const response = await api.serviceAccountService.listServiceAccountTokens(
                request,
                headers ? { headers: headers.headers } : undefined,
            );
            return {
                tokens: response.tokens.map(toPlainServiceAccountToken),
            };
        },
        enabled: enabled && !!serviceAccountId && !!headers,
        retry: defaultRetry,
        throwOnError: defaultThrowOnError,
    });
};

export type CreateServiceAccountTokenParams = {
    serviceAccountId: string;
    description: string;
    validForDays?: number;
};

export type CreateServiceAccountTokenResult = {
    serviceAccountToken: PlainServiceAccountToken;
    token: string;
};

export type UseCreateServiceAccountTokenParams = {
    headers: ServiceAccountHeaders;
};

export const useCreateServiceAccountToken = ({ headers }: UseCreateServiceAccountTokenParams) => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            description,
            validForDays = 30,
        }: CreateServiceAccountTokenParams): Promise<CreateServiceAccountTokenResult> => {
            const request = new CreateServiceAccountTokenRequest({
                description,
                validFor: {
                    seconds: BigInt(validForDays * 24 * 60 * 60),
                    nanos: 0,
                },
            });

            const response = await api.serviceAccountService.createServiceAccountToken(request, {
                headers: headers.headers,
            });

            if (!response.serviceAccountToken) {
                throw new Error("Error creating service account token");
            }

            return {
                serviceAccountToken: toPlainServiceAccountToken(response.serviceAccountToken),
                token: response.token,
            };
        },
        onSuccess: (result, variables) => {
            queryClient.setQueryData(
                serviceAccountTokensQueryKey.list(variables.serviceAccountId),
                (currentData?: { tokens: PlainServiceAccountToken[] }) => {
                    if (!currentData) {
                        return { tokens: [result.serviceAccountToken] };
                    }
                    return { tokens: [result.serviceAccountToken, ...currentData.tokens] };
                },
            );
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type DeleteServiceAccountTokenParams = {
    serviceAccountId: string;
    tokenId: string;
};

export type UseDeleteServiceAccountTokenParams = {
    headers: ServiceAccountHeaders;
};

export const useDeleteServiceAccountToken = ({ headers }: UseDeleteServiceAccountTokenParams) => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ tokenId }: DeleteServiceAccountTokenParams) => {
            const request = new DeleteServiceAccountTokenRequest({ serviceAccountTokenId: tokenId });
            await api.serviceAccountService.deleteServiceAccountToken(request, { headers: headers.headers });
            return tokenId;
        },
        onSuccess: (deletedTokenId, variables) => {
            queryClient.setQueryData(
                serviceAccountTokensQueryKey.list(variables.serviceAccountId),
                (currentData?: { tokens: PlainServiceAccountToken[] }) => {
                    if (!currentData) {
                        return currentData;
                    }
                    return {
                        tokens: currentData.tokens.filter((token) => token.id !== deletedTokenId),
                    };
                },
            );
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};
